/**
 * Tournament types for frontend
 */

import type { TimeControlPreset, OpeningMode, EsitoStorico } from '@draughts/shared';
import { GameVariant } from '@draughts/shared';

/**
 * Tournament status
 */
export type TournamentStatus = 'scheduled' | 'active' | 'finished' | 'cancelled';

/**
 * Tournament format
 */
export type TournamentFormat = 'arena' | 'swiss' | 'round_robin';

/** Pubblico o privato. Il privato si vede lo stesso: cambia come ci si entra. */
export type TournamentVisibility = 'public' | 'private';

/**
 * Participant status
 */
export type ParticipantStatus = 'active' | 'paused' | 'withdrawn';

/**
 * Arena scoring constants
 */
export const ARENA_SCORING = {
  WIN: 2,
  DRAW: 1,
  LOSS: 0,
} as const;

/**
 * Vedi `VITTORIA_A_TURNI` in `packages/shared/src/types/tournament.ts`: qui la
 * costante intermedia si ripete perche' questa e' una copia INDIPENDENTE, non
 * un re-export.
 */
const VITTORIA_A_TURNI = 2;

/**
 * Punteggio dei tornei a turni: **2 / 1 / 0**, la scala della Federazione
 * Italiana Dama (non piu' la FIDE 1 / 0,5 / 0).
 *
 * ⚠️ Copia **indipendente** di `SWISS_SCORING` di `@draughts/shared`: nessun
 * componente web importa quella condivisa, e questi valori sono un secondo
 * letterale, non lo stesso oggetto raggiunto per due strade. La deriva fra le
 * due la fermano `swiss-scoring-coerenza.test.ts` (accanto a questo file) e il
 * gemello dell'arena; il commento di quel file spiega perche' la pinza va messa
 * dal lato web e non da `shared`.
 */
export const SWISS_SCORING = {
  WIN: VITTORIA_A_TURNI,
  DRAW: 1,
  LOSS: 0,
  /** «Come una vittoria», e legato a `WIN` per costruzione. */
  BYE: VITTORIA_A_TURNI,
} as const;

/**
 * Tournament list item
 */
export interface TournamentListItem {
  id: string;
  name: string;
  description: string | null;
  timeControl: TimeControlPreset;
  variant: string;
  durationMinutes: number;
  status: TournamentStatus;
  startsAt: string;
  endsAt: string | null;
  playerCount: number;
  gamesCount: number;
  minRating: number;
  maxRating: number;
  isRated: boolean;
  creatorUsername: string | null;
  /**
   * Chi ha vinto. Valorizzato solo sui tornei conclusi — la colonna si scrive
   * alla fine del torneo (migration 097) con il primo della classifica
   * annunciata. Resta null anche su un concluso se quell'account e' stato
   * cancellato: il torneo e' accaduto lo stesso.
   */
  winnerUsername?: string | null;
  /** Perche' e' stato annullato; il server lo manda nel dettaglio. */
  cancelReason?: 'organizer' | 'no_players' | null;
  /**
   * A che posto sei arrivato. Solo sui tornei conclusi a cui hai partecipato,
   * e solo nello SVIZZERO: nel girone gli spareggi non sono colonne e un
   * numero calcolato sull'elenco potrebbe contraddire la classifica vera.
   */
  myRank?: number | null;
  isRegistered?: boolean;
  format?: TournamentFormat;
  currentRound?: number;
  totalRounds?: number;
  /**
   * STORIA: il girone che ripeteva il CALENDARIO, raddoppiando i turni.
   *
   * Dalla migration 113 nessuno la scrive piu' — anche il girone rigioca
   * dentro il turno, e la parola e' `twoLeg` — ma i gironi FINITI del vecchio
   * mondo ce l'hanno ancora, ed e' l'unica cosa che li etichetta «andata e
   * ritorno». Se ne va quando cade la colonna.
   */
  doubleRound?: boolean;
  /**
   * Andata e ritorno: lo stesso accoppiamento rigiocato DENTRO il turno, a
   * colori invertiti e sulla stessa apertura. Vale per tutti e due i formati a
   * turni (migration 105 lo svizzero, 113 il girone). Facoltativo come campo
   * di filo.
   */
  twoLeg?: boolean;
  /**
   * La soglia dell'italo-svizzero (migration 107): da quanti iscritti in su si
   * gioca svizzero invece che girone.
   *
   * Al web il NUMERO non serve — i turni li fissa il server all'ora d'inizio —
   * ma serve sapere che c'e': finche' il torneo e' programmato, un valore qui
   * vuol dire che `format` non e' ancora una decisione presa, e l'etichetta
   * giusta e' «Italo-svizzero» invece del nome di un formato che potrebbe
   * cambiare all'avvio.
   */
  swissThreshold?: number | null;
  /**
   * I TURNI EXTRA (migration 116): quanti turni in piu' di quelli della
   * tabella — 0, 1 o 2 — quando il campo li regge.
   *
   * E' una RICHIESTA, non una promessa — `turniSvizzeroPerIscritti` li ignora
   * se gli iscritti non bastano — e vale solo insieme a `swissThreshold`, che
   * e' quello che il CHECK della 116 pretende. Fino alla 108 non usciva da
   * nessun DTO: il server lo applicava e nessuno poteva leggerlo.
   */
  swissExtraRounds?: number;
  maxPlayers?: number | null;
  /**
   * Il pavimento degli iscritti: sotto questo numero il torneo si annulla da
   * solo all'ora d'inizio. Insieme a `maxPlayers` e' l'intervallo dei posti.
   */
  minPlayers?: number | null;
  /**
   * Un torneo privato si VEDE nella lista — sapere che esiste non e' il
   * segreto — ma per entrarci serve il link o la password.
   */
  visibility?: TournamentVisibility;
  /**
   * Un EVENTO SPECIALE: la gara e' nata dal calendario, non da una persona.
   *
   * Lo decide il server leggendo `created_by IS NULL` — la stessa assenza che
   * fa sparire la riga «Creato da», detta finalmente per nome. Il client non
   * deve dedurla da `creatorUsername` nullo, che si spegne anche quando
   * l'account di chi ha aperto la gara e' stato cancellato.
   *
   * Facoltativo come gli altri campi di filo: durante un deploy il JS nuovo
   * parla col Node vecchio, e assente vuol dire «taci», mai «probabilmente
   * si'».
   */
  eventoSpeciale?: boolean;
  /**
   * Con che apertura si gioca. Serve alla card dell'evento sotto la hero, che
   * disegna la stessa pastiglia per tornei e arene.
   */
  openingMode?: OpeningMode;
  /**
   * QUALE apertura, e solo con `openingMode: 'single'` (migration 112).
   *
   * Senza il numero la pastiglia direbbe «Apertura unica» e basta: il tema del
   * torneo senza il tema. E' l'unica apertura che non si descrive da sola.
   */
  openingId?: number | null;
  /** Vero solo per chi ha aperto il torneo (`created_by`). Lo decide il server. */
  isOrganizer?: boolean;
  /*
   * Il gettone dell'invito, e la password: il server li consegna SOLO a chi ha
   * aperto il torneo (`segretiDelTorneo`), e per tutti gli altri non ci sono
   * proprio. Dichiararli qui non li rende pubblici — li rende leggibili a chi
   * li riceve, che finora doveva fingere che non esistessero.
   */
  inviteToken?: string | null;
  joinPassword?: string | null;
}

/**
 * Tournament standing entry
 */
export interface TournamentStanding {
  rank: number;
  userId: string;
  username: string;
  avatar?: string | null;
  rating: number;
  points: number;
  gamesPlayed: number;
  gamesWon: number;
  gamesDrawn: number;
  gamesLost: number;
  currentStreak: number;
  isPlaying: boolean;
  isAvailable: boolean;
  /**
   * Che cosa sta facendo nel turno in corso (tornei a turni, torneo attivo):
   * lo calcola il server (stato-in-classifica.ts). `null`/assente = niente da
   * dire, cioe' collegato e fra un turno e l'altro.
   */
  stato?: 'in-gioco' | 'assente' | 'ha-finito' | 'riposa' | 'disconnesso' | null;
  // Swiss tiebreaker fields
  buchholz?: number;
  sonnebornBerger?: number;
  /** Rating guadagnato (o perso) nelle partite di questo torneo: il quinto spareggio. */
  variazioneRating?: number;
  /**
   * Solo nel girone: chi questo giocatore ha superato per lo SCONTRO DIRETTO
   * (stessi punti e vittorie). Assente quando lo scontro diretto non ha deciso.
   */
  scontroDirettoSu?: string[];
  /**
   * L'esito di ogni partita, in ordine cronologico: lo score sheet della riga
   * (migration 122, `results_history`).
   *
   * Vuoto per i tornei nati prima della colonna, e piu' corto di
   * `gamesPlayed` per chi era iscritto a cavallo della migration: la fila si
   * disegna solo quando e' COMPLETA, perche' tre pedine accanto a cinque
   * partite giocate sono una bugia. `bye` e' il riposo — non e' una partita,
   * ma conta come tale.
   */
  resultsHistory?: EsitoStorico[];
}

/**
 * My participant data
 */
export interface MyParticipant {
  points: number;
  gamesPlayed: number;
  gamesWon: number;
  gamesDrawn: number;
  gamesLost: number;
  currentStreak: number;
  bestStreak: number;
  status: ParticipantStatus;
}

/**
 * Tournament detail (full data)
 */
export interface TournamentDetail {
  tournament: TournamentListItem;
  standings: TournamentStanding[];
  allParticipants: TournamentStanding[];
  participantCount: number;
  isJoined: boolean;
  myParticipant: MyParticipant | null;
}

/**
 * Quello che chi crea un torneo sceglie.
 *
 * La DURATA non c'e', ed e' voluto: la calcola il server dalla stima, che e' la
 * stessa che il modale mostra mentre si compila. Cosi' il tempo dopo cui il
 * torneo si chiude e' coerente con il numero che chi crea ha letto.
 */
export interface CreateTournamentInput {
  name: string;
  description?: string;
  /**
   * Uno solo, ed e' un difetto in attesa: la riga nasce svizzera e l'avvio la
   * fa retrocedere a girone se si presentano in meno della soglia. Il girone
   * non si CHIEDE piu' — ci si arriva dal campo qui sotto.
   */
  format: 'swiss';
  timeControl: TimeControlPreset;
  variant: string;
  /**
   * L'ora d'inizio, SCELTA: un istante ISO, e si manda com'e'.
   *
   * ⚠️ Accanto a questo campo c'era `startsInMinutes`, lo scarto in minuti che
   * l'istante lo faceva calcolare al SERVER. Nacque da un difetto misurato in
   * produzione — `Date.now() + scarto` dipende dall'orologio del telefono di
   * chi crea, e i tornei partivano da 8 secondi a quasi due minuti PRIMA dello
   * scarto scelto, mai dopo — e quel difetto era degli SCARTI: un'ora scritta
   * non legge l'orologio, legge il fuso.
   *
   * Il modale adesso chiede soltanto data e ora, quindi il client manda sempre
   * questa. Lo SCHEMA DEL SERVER accetta ancora i minuti, e la differenza e'
   * voluta: una PWA col JS vecchio in cache che manda ancora lo scarto non deve
   * prendersi un 400.
   */
  startsAt: string;
  /**
   * Andata e ritorno, in UNA parola sola.
   *
   * `doubleRound` non si manda piu': chi crea non sa ancora se il suo torneo
   * sara' un girone, quindi non puo' nemmeno scegliere fra le due parole del
   * server. Ne chiede una — «le stesse due persone rigiocano a colori
   * invertiti?» — e quale colonna scrivere lo decide l'avvio.
   */
  twoLeg?: boolean;
  /**
   * I posti. **Obbligatori**: senza un tetto non esiste nessun caso peggiore da
   * cui ricavare la durata, e i turni ormai si ricavano dagli iscritti.
   */
  maxPlayers: number;
  /**
   * La SOGLIA, al posto del vecchio «max turni»: da questo numero di iscritti
   * in su si gioca svizzero, sotto girone. Da 7 a 20.
   */
  swissThreshold: number;
  /**
   * Quanti turni in piu' di quelli della tabella — 0, 1 o 2 — se all'avvio il
   * torneo esce svizzero.
   *
   * Non e' il ritorno dei turni dichiarati: qui non viaggia un numero di turni
   * ma un `+n` su un numero che resta derivato dagli iscritti veri, e ha
   * comunque il tetto che l'accoppiatore puo' onorare. Sul girone non ha
   * effetto: la' il calendario e' completo, e chi vuole rigiocarlo chiede
   * l'andata e ritorno.
   */
  swissExtraRounds?: number;
  /**
   * Chi crea il torneo ci gioca, salvo dirlo. Il server lo tiene a `true` se
   * manca: tre tornei veri sono morti con zero iscritti perche' crearne uno
   * non iscriveva nemmeno chi l'aveva aperto.
   */
  joinAsPlayer?: boolean;
  /**
   * Il minimo per partire, che chi organizza puo' solo ALZARE: il server tiene
   * il suo pavimento (`PAVIMENTO_ISCRITTI_TORNEO`, TRE — e tre e' anche quello
   * dell'arena) e applica `max(pavimento, richiesto)`. Assente = decide il
   * server; sotto 3 il server rifiuta.
   *
   * ⚠️ Il pavimento del torneo e' stato 4 due volte — prima sulle sole gare
   * valutate, poi su tutte — e quel `max()` riscriveva in silenzio un tre
   * chiesto. Adesso i due pavimenti coincidono e il `max()` non riscrive piu'
   * niente: serve solo per la chiave che non arriva affatto.
   */
  minPlayers?: number;
  isRated?: boolean;
  /**
   * Lo SBARRAMENTO: il punteggio sotto cui non si entra. Zero = nessuno.
   *
   * Una voce di `SBARRAMENTI` (shared), non un numero qualsiasi: lo schema del
   * server confronta con quella lista. Vale solo sui tornei valutati — sugli
   * amichevoli il server lo azzera, e il modale non manda altro che zero.
   */
  minRating?: number;
  /** L'apertura, nella stessa lingua della partita personalizzata. */
  opening?: OpeningMode;
  /** Quale apertura, e solo con `opening: 'single'`: il numero di tabella (1-174). */
  openingId?: number;
  visibility?: TournamentVisibility;
  joinPassword?: string;
}

/** La risposta alla creazione: le due chiavi arrivano solo a chi ha creato. */
export interface CreatedTournament {
  id: string;
  name: string;
  format: TournamentFormat;
  startsAt: string;
  estimatedRounds: number;
  visibility: TournamentVisibility;
  joinPassword: string | null;
  inviteToken: string | null;
}

/**
 * Forced opening data (sorteggio)
 */
export interface ForcedOpeningData {
  id: number;
  moves: string[];
  forza: string;  // P, N, NN, NNN, B, BB, BBB
  displayText: string;
}

/**
 * WebSocket: Tournament match data
 */
export interface TournamentMatchData {
  gameId: string;
  roomId: string;
  opponentId: string;
  opponentUsername: string;
  opponentRating: number;
  opponentAvatar?: string | null;
  color: 'white' | 'black';
  forcedOpening?: ForcedOpeningData;
  roundNumber?: number;
  /**
   * Quale manche: 1 andata, 2 ritorno. Vale 1 nei tornei a manche unica.
   *
   * Serve a dire «1/2» sotto la scacchiera, e il caso per cui esiste e' il
   * RITORNO: quello parte da solo, senza l'overlay «trovato avversario», e
   * senza questo campo l'unico segnale che si sta giocando la seconda partita
   * sarebbe il colore invertito.
   */
  leg?: number;
  /**
   * Quante manche ha questo accoppiamento: 2 solo in andata e ritorno, 1
   * altrimenti. Assente dai server piu' vecchi del campo, dove vale 1.
   *
   * Senza il totale, `leg` da solo non dice niente: vale 1 anche a manche
   * unica, e il client che lo leggeva come «c'e' una manche» annunciava «1/2»
   * su ogni partita di torneo.
   */
  totalLegs?: number;
}

/**
 * WebSocket: Tournament standings update
 */
export interface TournamentStandingsUpdate {
  tournamentId: string;
  standings: TournamentStanding[];
  participantCount: number;
  myRank: number | null;
  myStats: {
    points: number;
    gamesPlayed: number;
    streak: number;
  } | null;
}

/**
 * WebSocket: Tournament ended data
 */
export interface TournamentEndedData {
  tournamentId: string;
  standings: TournamentStanding[];
  winner: {
    userId: string;
    username: string;
    points: number;
  } | null;
}

/**
 * Tournament game entry (for Games tab)
 */
export interface TournamentGameEntry {
  gameId: string;
  whiteUserId: string;
  whiteUsername: string;
  /**
   * Nulli quando quel giocatore ha cancellato l'account (migration 076: l'id va
   * a NULL e il LEFT JOIN non trova più la riga in `users`). Il server li
   * mandava a 0 e la scheda «Partite» stampava «(0)».
   */
  whiteRating: number | null;
  blackUserId: string;
  blackUsername: string;
  blackRating: number | null;
  /** 'forfeit' = doppio no-show: nessuno dei due si è presentato, 0 punti a entrambi. */
  result: 'white' | 'black' | 'draw' | 'forfeit' | null;
  movesCount: number;
  /**
   * `games.id`: l'unico id con cui si apre la review della partita.
   *
   * **Non e' `gameId`.** Quello e' l'id di STANZA, la chiave di
   * `tournament_games`, e `GET /api/games/:id` non lo conosce. Sono tutti e due
   * `string`, quindi uno scambio non lo vede il compilatore: la riga si apre
   * con QUESTO.
   *
   * Facoltativo perche' e' un campo di filo (deploy: JS nuovo, Node vecchio), e
   * nullo a regime per le partite anteriori alla migration 084 che il recupero
   * non ha saputo riagganciare senza ambiguita'.
   */
  savedGameId?: string | null;
  roundNumber: number;
  /**
   * Quale manche: 1 andata, 2 ritorno. Vale 1 nei tornei a manche unica.
   *
   * Facoltativo perche' e' un campo di filo — durante un deploy il web nuovo
   * parla con un server vecchio — e perche' la scheda «Partite» lo usa solo
   * per etichettare: senza, mostra la riga come ha sempre fatto.
   *
   * Arriva da due strade che devono dire la stessa cosa: la REST
   * (`getTournamentGames`) al caricamento, e l'evento `tournament:game-completed`
   * dal vivo. Se una delle due lo dimenticasse, la pastiglia comparirebbe solo
   * dopo un ricaricamento.
   */
  leg?: number;
  createdAt: string;
}

/**
 * Format duration for display
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${mins}min`;
}

/**
 * Format time until tournament starts
 */
export function formatTimeUntil(startsAt: string): string {
  const now = new Date();
  const start = new Date(startsAt);
  const diff = start.getTime() - now.getTime();

  if (diff <= 0) {
    return 'Starting now';
  }

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `in ${days}d ${hours % 24}h`;
  }
  if (hours > 0) {
    return `in ${hours}h ${minutes % 60}m`;
  }
  return `in ${minutes}m`;
}

/**
 * Format relative time (e.g., "2m ago", "1h ago")
 */
export function formatTimeAgo(dateStr: string): string {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diff = now - date;

  if (diff < 60000) return '<1m';
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}
